import reporttailorworkService from '../../../services/reporttailorworkService';
import filterService from '../../../services/filterService';

import {
    setReporttailorworkLoading,
    setReporttailorworkError,
    setReporttailorworkList,
    setReporttailorworkCurrent,
    setReporttailorworkPagination,
    setReporttailorworkSort,
    setReporttailorworkFilters,
    setReporttailorworkDefaultFilter,
    setReporttailorworkSettings,
    clearReporttailorworkCurrent,
    clearReporttailorworkError,
    setReporttailorworkFilterLoading,
    changeReporttailorworkFilterCondition,
    addReporttailorworkFilterCondition,
    updateReporttailorworkFilterConditionRule,
    updateReporttailorworkFilterConditionValue,
    removeReporttailorworkFilterCondition,
    setReporttailorworkSelected,
    setReporttailorworkDownloading,
    resetReporttailorworkFiltersAndPagination,

    setReporttailorworkClassicFilters
} from './reporttailorworkSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().reporttailorwork.isSettingsInitialized) {
            dispatch(setReporttailorworkLoading(true));
            const response = await reporttailorworkService.getSettings();

            // Set the settings
            dispatch(setReporttailorworkSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setReporttailorworkClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setReporttailorworkDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setReporttailorworkError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setReporttailorworkLoading(false));
    }
};

export const fetchReporttailorworks = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().reporttailorwork;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setReporttailorworkLoading(true));
        const response = await reporttailorworkService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setReporttailorworkList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchReporttailorworks:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReporttailorworkError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReporttailorworkLoading(false));
    }
};

export const fetchReporttailorwork = (id) => async (dispatch) => {
    try {
        dispatch(setReporttailorworkLoading(true));
        const response = await reporttailorworkService.getOne(id);
        dispatch(setReporttailorworkCurrent(response.data));
    } catch (error) {
        dispatch(setReporttailorworkError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setReporttailorworkLoading(false));
    }
};

export const createReporttailorwork = (data) => async (dispatch) => {
    try {
        const response = await reporttailorworkService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateReporttailorwork = (id, data) => async (dispatch) => {
    try {
        const response = await reporttailorworkService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteReporttailorwork = (id) => async (dispatch) => {
    try {
        dispatch(setReporttailorworkLoading(true));
        const response = await reporttailorworkService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setReporttailorworkError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReporttailorworkLoading(false));
    }
};

export const downloadReporttailorwork = (type) => async (dispatch, getState) => {
    try {
        const state = getState().reporttailorwork;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setReporttailorworkDownloading(true));
        const response = await reporttailorworkService.downloadAsync(finalQueryParams, filter, state.classicFilters);
        dispatch(setReporttailorworkDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading reporttailorworks:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setReporttailorworkError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setReporttailorworkDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setReporttailorworkPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setReporttailorworkSort(sort));
};

export const searchReporttailorworks = (query) => (dispatch, getState) => {
    const currentFilters = getState().reporttailorwork.filters;
    dispatch(setReporttailorworkFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setReporttailorworkDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeReporttailorworkFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addReporttailorworkFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateReporttailorworkFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateReporttailorworkFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeReporttailorworkFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setReporttailorworkFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().reporttailorwork.settings;
            dispatch(setReporttailorworkSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setReporttailorworkFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setReporttailorworkFilterLoading(false));
        dispatch(setReporttailorworkError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setReporttailorworkSelected(selectedRows));
    };
};

export const clearCurrentReporttailorwork = () => {
    return (dispatch) => {
        dispatch(clearReporttailorworkCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetReporttailorworkFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().reporttailorwork.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setReporttailorworkClassicFilters(updatedClassicFilters));
};